import { PaginationState, SortingState } from '@tanstack/react-table'
import { Modal } from 'antd'
import { TableComponent } from 'components/table/TableComponent'
import { ProcessFlowTableColumns } from 'features/processFlowDiagrams/components/ProcessFlowsTableColumns'
import { useRolodexEntries } from 'features/rolodex'
import React, { useState } from 'react'
import styled from 'styled-components'
import { GeckoProcessFlowDiagram, RolodexEntryType } from 'types'

const ModalContent = styled.div`
  margin-top: 1rem;
`

const ModalDescription = styled.p`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`

interface LinkExistingPFDModalProps {
  isOpen: boolean
  onConfirm: (pfdId: string) => void
  onCancel: () => void
  siteID: string
  excludePFDIds?: string[]
}

export const LinkExistingPFDModal: React.FC<LinkExistingPFDModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  siteID,
  excludePFDIds = [],
}) => {
  const [selectedPFDId, setSelectedPFDId] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([])

  const { processFlowDiagrams, isLoading } = useRolodexEntries({
    type: [RolodexEntryType.PROCESS_FLOW_DIAGRAM],
    tags: {
      site: siteID,
    },
  })

  // Filter out any PFDs that are already linked or should be excluded
  const availablePFDs = processFlowDiagrams.filter(
    (pfd) => !excludePFDIds.includes(pfd.id)
  )

  const handleRowClick = (entryId: string) => {
    setSelectedPFDId(entryId === selectedPFDId ? null : entryId)
  }

  const handleConfirm = () => {
    if (selectedPFDId) {
      onConfirm(selectedPFDId)
      setSelectedPFDId(null)
    }
  }

  const handleCancel = () => {
    setSelectedPFDId(null)
    onCancel()
  }

  const columns = ProcessFlowTableColumns<GeckoProcessFlowDiagram>({
    selectedRows: new Set(selectedPFDId ? [selectedPFDId] : []),
    onRowSelection: (entryId, isSelected) => {
      setSelectedPFDId(isSelected ? entryId : null)
    },
  })

  return (
    <Modal
      title='Link Existing Process Flow Diagram'
      open={isOpen}
      onOk={handleConfirm}
      onCancel={handleCancel}
      okText='Link'
      cancelText='Cancel'
      okButtonProps={{ disabled: !selectedPFDId }}
      width={800}>
      <ModalContent>
        <ModalDescription>
          Select an existing process flow diagram to link to this system. Only
          one diagram can be linked at a time.
        </ModalDescription>
        <TableComponent
          isLoadingData={isLoading}
          data={[...availablePFDs]}
          columns={columns}
          itemDescriptiveName='process flows'
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={(row) => {
            handleRowClick(row.original.id)
          }}
        />
      </ModalContent>
    </Modal>
  )
}
